import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ChevronDown, ChevronUp, Info } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const nf = (n) => Number(n || 0).toLocaleString('de-DE');
const fmtDay = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const ACTION_LABELS = {
  multichannel_text: 'Texte (alle Kanäle)', longform: 'Blog / Newsletter', press_release: 'Pressemitteilung',
  image_generate: 'Bild erzeugen', image_edit: 'Bild ändern', text_edit: 'Text ändern', video_generate: 'Video',
  pin_title: 'Pin-Titel', brand_dna_analyze: 'Brand DNA neu analysieren', image_retry_first: 'Erster Bild-Retry (gratis)',
  initial_grant: 'Start-Guthaben', cycle_reset: 'Monatliche Plan-Credits', plan_assigned: 'Plan gesetzt',
  migration_reset: 'Umstellung auf das neue Credit-Modell', admin_grant: 'Geschenk', purchase: 'Credit-Paket',
  gift_expired: 'Geschenk abgelaufen', admin_correction: 'Korrektur',
  // altes Modell
  content_single: 'Content erzeugen', text_regenerate: 'Text neu schreiben', text_refine: 'Text ändern',
  regenerate_field: 'Feld neu schreiben', image_refine: 'Bild ändern',
};
const PRICE_LINES = [
  ['multichannel_text', 'Texte für alle gewählten Kanäle (pro Eingabe)'], ['longform', 'Blog oder Newsletter (je Artikel)'],
  ['press_release', 'Pressemitteilung'], ['image_generate', 'Bild erzeugen'], ['image_edit', 'Bild per Prompt ändern'],
  ['text_edit', 'Text per Prompt ändern'], ['brand_dna_analyze', 'Brand DNA neu analysieren'],
];

const Card = ({ title, children, testId }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={testId}>
    <div className="px-6 py-4 border-b border-gray-200"><h4 className="text-base font-semibold text-gray-900">{title}</h4></div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

/** Abrechnung der Credits für den Kunden: Stand der Töpfe, Verbrauch je Aktion, Preise, Verlauf. */
export default function CreditsStatement({ getAuthHeader }) {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const PAGE = 20;

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/credits/summary`, { headers: getAuthHeader() }),
        axios.get(`${API}/credits/ledger`, { headers: getAuthHeader(), params: { limit: PAGE } }),
      ]);
      setSummary(s.data);
      setEntries(l.data.entries || []);
      setMore((l.data.entries || []).length === PAGE);
    } catch (e) { /* Abschnitt bleibt leer */ } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const last = entries[entries.length - 1];
      const { data } = await axios.get(`${API}/credits/ledger`, { headers: getAuthHeader(), params: { limit: PAGE, before: last?.created_at } });
      setEntries((cur) => [...cur, ...(data.entries || [])]);
      setMore((data.entries || []).length === PAGE);
    } finally { setLoadingMore(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#1e9ead]" /></div>;
  if (!summary) return null;

  const legacy = summary.pricing === 'legacy';
  return (
    <div className="space-y-6" data-testid="credits-statement">
      {legacy && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          Dein Konto nutzt noch das bisherige Credit-Modell. Ab dem {fmtDay(summary.credits_reset_date)} stellen wir auf das neue Modell mit neuer
          Monatsmenge und neuen Preisen um. Bis dahin gelten die bisherigen Preise.
        </div>
      )}

      <Card title="Dein Guthaben" testId="credits-balance">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-3xl font-black text-gray-900" data-testid="credits-available">{nf(summary.credits_available)}</div>
            <div className="text-sm text-gray-500">Credits verfügbar</div>
          </div>
          {!legacy && (
            <div className="text-sm text-gray-600 space-y-1 sm:text-right">
              <div>Plan-Credits: <span className="font-semibold text-gray-900">{nf(summary.plan_credits)}</span>
                {summary.credits_total > 0 && <span className="text-gray-400"> von {nf(summary.credits_total)}</span>}</div>
              {summary.purchased_credits > 0 && <div>Gekaufte Credits: <span className="font-semibold text-gray-900">{nf(summary.purchased_credits)}</span> <span className="text-gray-400">(verfallen nicht)</span></div>}
              {summary.gift_credits > 0 && <div>Geschenkte Credits: <span className="font-semibold text-gray-900">{nf(summary.gift_credits)}</span>
                {summary.gift_expires_at && <span className="text-gray-400"> (bis {fmtDay(summary.gift_expires_at)})</span>}</div>}
              {summary.credits_reset_date && <div className="text-gray-500">Plan-Credits werden am {fmtDay(summary.credits_reset_date)} auf die Monatsmenge gesetzt.</div>}
            </div>
          )}
        </div>
      </Card>

      {!legacy && (
        <Card title="Verbrauch in diesem Zeitraum" testId="credits-usage">
          {summary.by_action.length === 0 ? (
            <p className="text-sm text-gray-500">Noch nichts verbraucht.</p>
          ) : (
            <div className="space-y-2">
              {summary.by_action.map((a) => (
                <div key={a.action_type} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{ACTION_LABELS[a.action_type] || a.action_type} <span className="text-gray-400">({a.count}×)</span></span>
                  <span className="font-semibold text-gray-900">{nf(a.credits)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm pt-2 mt-2 border-t border-gray-100">
                <span className="text-gray-500">Gesamt</span><span className="font-bold text-gray-900">{nf(summary.spent_this_cycle)}</span>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card title="Verlauf" testId="credits-ledger">
        {!legacy && summary.prices && (
          <div className="mb-4" data-testid="credits-prices">
            <button onClick={() => setShowPrices((v) => !v)} className="inline-flex items-center gap-1.5 text-sm text-[#1e9ead] hover:underline" aria-expanded={showPrices}>
              <Info className="w-4 h-4" />Was kostet eine Aktion?
              {showPrices ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showPrices && (
              <div className="mt-3 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 space-y-1.5 text-sm">
                {PRICE_LINES.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <span className="text-gray-700">{label}</span>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">{nf(summary.prices[key])} Credits</span>
                  </div>
                ))}
                <p className="text-xs text-gray-500 pt-2">Der erste Bild-Retry pro Beitrag ist gratis. Videos laufen über das Video-Kontingent deines Plans und kosten keine Credits.</p>
              </div>
            )}
          </div>
        )}
        {entries.length === 0 ? (
          <p className="text-sm text-gray-500">Noch keine Bewegungen.</p>
        ) : (
          <>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-2 py-2 font-medium">Datum</th><th className="px-2 py-2 font-medium">Aktion</th>
                    <th className="px-2 py-2 font-medium text-right">Credits</th><th className="px-2 py-2 font-medium text-right">Stand</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100">
                      <td className="px-2 py-2 text-gray-500 whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                      <td className="px-2 py-2 text-gray-800">
                        {ACTION_LABELS[e.action_type] || e.action_type}
                        {e.qty > 1 && <span className="text-gray-400"> ×{e.qty}</span>}
                        {(e.content_numbers || []).length > 0 && (
                          <span className="ml-1.5 font-mono text-xs text-[#1e9ead]" data-testid="ledger-content-numbers">{e.content_numbers.map((n) => `#${n}`).join(', ')}</span>
                        )}
                        {e.note && <div className="text-xs text-gray-400">{e.note}</div>}
                      </td>
                      <td className={`px-2 py-2 text-right font-semibold whitespace-nowrap ${e.credits_delta > 0 ? 'text-green-600' : e.credits_delta < 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                        {e.credits_delta > 0 ? '+' : ''}{nf(e.credits_delta)}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-500 whitespace-nowrap">{nf(e.balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {more && (
              <button onClick={loadMore} disabled={loadingMore} className="mt-3 text-sm text-[#1e9ead] hover:underline">
                {loadingMore ? 'Lädt …' : 'Mehr anzeigen'}
              </button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
